<script lang="ts">
	import classNames from 'classnames';
	import { onMount, setContext, type Component } from 'svelte';
	import { writable, type Writable } from 'svelte/store';
	import { twMerge } from 'tailwind-merge';
	import { GlobalRoutes } from '../../constants/global-routes';
	import { NavigationMixpanelEvents } from '../../constants/mixpanel-events';
	import { isGlobalNavHidden } from '../../utils/isGlobalNavHidden/isGlobalNavHidden';
	import { getGlobalLinks } from './utils/getGlobalLinks/getGlobalLinks';
	import { Contexts } from '#lib/constants/contexts.js';
	import { trackOnwardClicks } from '#lib/features/analytics/utils/trackOnwardClicks/trackOnwardClicks.js';
	import { getLiveDropInfoContext } from '#lib/features/live-drop-info/LiveDropInfoContext/utils/getLiveDropInfoContext/getLiveDropInfoContext.js';
	import { beforeNavigate } from '$app/navigation';
	import { page, navigating } from '$app/state';
	import { HeniLogo } from '$global/assets/logos/HeniLogo';
	import { IconLink } from '$global/components/IconLink';
	import { config } from '$global/config/heni-websites-config';
	import { BottomBar } from '$global/features/navigation/components/BottomBar';
	import { SearchTopBar } from '$global/features/navigation/components/SearchTopBar';
	import { TopBar } from '$global/features/navigation/components/TopBar';
	import type { TopBarClasses } from '$global/features/navigation/components/TopBar/TopBar.svelte';
	import { TopBarContainer } from '$global/features/navigation/components/TopBarContainer';
	import type { SiteNavLink } from '$global/features/navigation/types';
	import {
		createNavPopover,
		type NavPopover,
	} from '$global/features/navigation/utils/createNavPopover';
	import type { NavBarContext } from '$global/features/navigation/utils/getNavBarContext/getNavBarContext';
	import type { SearchClasses } from '$global/features/search/components/QuerySearch/Search';
	import type { SearchCombobox } from '$global/features/search/utils/createSearchCombobox';
	import { shallowUpdateInFlight } from '$global/runes/shallowUpdateInFlight/shallowUpdateInFlight.svelte';
	import { isShallowUpdate } from '$global/utils/isShallowUpdate/isShallowUpdate';

	let {
		popover = undefined,
		SearchComponent = undefined,
		dark = undefined,
		siteLinks = [],
		isSearchEnabled = undefined,
		showMobileSearch = writable(false),
		combobox = undefined,
		classes = {},
		forceShowGlobalNav = undefined,
		keepMobileTriggersInAppMode = false,
		searchComponentProps = {},

		children,
		...rest
	}: Props = $props();

	interface Props {
		popover?: NavPopover | undefined;
		SearchComponent?:
			| Component<{
					combobox: SearchCombobox | undefined;
					onCloseSearch?: (() => void) | undefined;
					onclick?: () => void;
					onkeyup?: () => void;
					class?: string;
					classes?: SearchClasses | undefined;
					forceShowGlobalNav?: boolean | undefined;
			  }>
			| undefined;
		forceShowGlobalNav?: boolean | undefined;
		// App-webview visitors (global_nav=0) lose the mobile search and menu
		// triggers, since both open the global sections they must not see. News
		// opts out: its menu carries account actions, not the global nav.
		keepMobileTriggersInAppMode?: boolean;
		// Extra props forwarded verbatim to every SearchComponent instance
		// (rendered both here and inside BottomBar).
		searchComponentProps?: { forceShowGlobalNav?: boolean | undefined };
		dark?: boolean | undefined;
		siteLinks?: SiteNavLink[];
		isSearchEnabled?: boolean | undefined;
		showMobileSearch?: Writable<boolean>;
		combobox?: SearchCombobox | undefined;
		homeIcon?: import('svelte').Snippet;
		rightLink?: import('svelte').Snippet;
		leftLink?: import('svelte').Snippet;
		children?: import('svelte').Snippet;
		class?: string;
		classes?: {
			bottomBarClasses?: {
				siteLinks?: string;
				search?: string;
				siteLinksMenu?: string;
			};
			bottomBar?: string;
			topBar?: string;
			topBarClasses?: TopBarClasses;
		};
	}

	let { liveDropInfoCtx } = getLiveDropInfoContext();
	let liveDropInfo = $derived(liveDropInfoCtx.liveDropInfo);

	let globalLinks = $derived(getGlobalLinks(liveDropInfo));

	$effect(() => {
		if (page.url.pathname || page.url.search) {
			showMobileSearch.set(false);
		}
	});

	// The URL param is the fallback authority: on error pages (and any route
	// whose load doesn't provide showGlobalNav) page.data is empty, and app-mode
	// (global_nav=0) visitors must not be shown the global nav sections.
	let showGlobalNav = $derived(
		forceShowGlobalNav ??
			page.data.showGlobalNav ??
			!isGlobalNavHidden(page.url)
	);
	let hideMobileTriggers = $derived(
		!showGlobalNav && !keepMobileTriggersInAppMode
	);
	let comboboxOpen = $derived(combobox?.states.open);

	const toggleMobileSearch = () => {
		showMobileSearch.set(!$showMobileSearch);
	};

	const handleClickBack = () => {
		toggleMobileSearch();
		comboboxOpen?.set?.(false);
	};

	const defaultPopover = createNavPopover();

	// `popover` is a static prop set once at mount; the context is established for the component's lifetime.
	// svelte-ignore state_referenced_locally
	const navPopover = popover || defaultPopover;

	const context: NavBarContext = {
		popover: navPopover,
	};

	setContext(Contexts.NavBar, context);

	onMount(() => {
		const mediaQuery = window.matchMedia(
			`(max-width: ${config.theme.screens.lg})`
		);

		const listener: Parameters<typeof mediaQuery.addEventListener>[1] = (e) => {
			if (!(e as Event & { matches: boolean }).matches) {
				showMobileSearch.set(false);
			}
		};

		mediaQuery.addEventListener('change', listener);
		return () => {
			mediaQuery.removeEventListener('change', listener);
		};
	});

	beforeNavigate((navigation) => {
		// A shallow update only changes the URL or history state of the page being
		// viewed, so the menu the visitor may have open stays open.
		if (isShallowUpdate(navigation)) return;

		popover?.close();
	});

	// The progress indicator is for navigations that load a page, not for shallow
	// URL or history-state updates on the current one.
	let isNavigating = $derived(
		!!navigating.complete && shallowUpdateInFlight.count === 0
	);

	// The global nav is the cross-section jump the ticket calls out alongside the
	// News→commerce links: it is available from every page, so a click here is an
	// onward journey whatever section the visitor started in (HB-967).
	//
	// Delegated rather than per-link because the anchors are rendered four
	// components deep (TopBar → SiteLink → MenuItem) across desktop, dropdown and
	// mobile-menu variants; one listener per bar covers them all.
	// Left enabled even when `showGlobalNav` is false: that flag only hides the
	// top bar's cross-section menu, while the BottomBar's platform links stay
	// clickable, and a crossing made from those is as real as any other.
	const onwardClickParams = {
		eventName: NavigationMixpanelEvents.OnwardClick,
		sourceElement: 'global-nav',
		// `fromSection` is omitted deliberately — `trackOnwardClick` resolves the
		// origin from the current page, which is what the nav wants, since the
		// section the visitor is leaving changes with every navigation.
	};
</script>

<!--
	`display: contents` so the wrapper carries the delegated click listener
	without introducing a box that would break the sticky/flex layout of the bars
	inside it.
-->
<div class="contents" use:trackOnwardClicks={onwardClickParams}>
	<TopBarContainer
		dark={$showMobileSearch ? false : dark}
		class={twMerge(
			classNames({
				'z-30': $showMobileSearch,
				'fixed lg:sticky': comboboxOpen && $comboboxOpen && showGlobalNav,
				'lg:hidden': !showGlobalNav,
			}),
			rest.class
		)}
	>
		{#if $showMobileSearch && SearchComponent}
			<SearchTopBar onClickBack={handleClickBack}>
				<SearchComponent
					{combobox}
					{...searchComponentProps}
					onCloseSearch={toggleMobileSearch}
				/>
			</SearchTopBar>
		{:else}
			<TopBar
				{showGlobalNav}
				class={twMerge(
					classNames({ '[&>div>button]:hidden': hideMobileTriggers }),
					classes?.topBar
				)}
				classes={classes.topBarClasses}
				pathname={page?.url?.pathname}
				navigating={isNavigating}
				{dark}
				{siteLinks}
				homeRoute={GlobalRoutes.Home}
				{globalLinks}
				onClickSearch={toggleMobileSearch}
				isSearchEnabled={hideMobileTriggers ? false : isSearchEnabled}
				showMenuLabel
			>
				{#snippet homeIcon()}
					{@render rest?.homeIcon?.()}
				{/snippet}
				{#snippet leftLink()}
					{@render rest?.leftLink?.()}
				{/snippet}
				{#snippet rightLink()}
					{@render rest?.rightLink?.()}
				{/snippet}
			</TopBar>
		{/if}
	</TopBarContainer>
	<!--
	The platform sub-nav pins directly beneath the dark global bar (`top-12`
	matches that bar's 3rem height), so both stay visible while scrolling.
	`sticky` also positions the bar, which keeps the absolute nav-progress bar
	anchored to it rather than to the viewport-sized initial containing block
	(which placed it ~100vh down the page).
-->
	<BottomBar
		pathname={page?.url?.pathname}
		navigating={isNavigating}
		{siteLinks}
		{combobox}
		{SearchComponent}
		{searchComponentProps}
		class={twMerge('sticky top-12', classes.bottomBar)}
		classes={{
			siteLinks: classes.bottomBarClasses?.siteLinks,
			search: classes.bottomBarClasses?.search,
			siteLinksMenu: classes.bottomBarClasses?.siteLinksMenu,
		}}
	>
		{#snippet homeIcon()}
			{#if rest.homeIcon && !dark}
				{@render rest.homeIcon()}
			{:else}
				<IconLink
					dataCy="header"
					href={GlobalRoutes.Home}
					label="See HENI home page"
					class="outline-none"
				>
					<HeniLogo class="xs:scale-100 origin-left scale-75" />
				</IconLink>
			{/if}
		{/snippet}
		{#snippet rightLink()}
			{@render rest?.rightLink?.()}
		{/snippet}
	</BottomBar>
</div>
